import { api, ApiError } from '@/lib/api';
import { currentCycle } from '@/lib/document-naming';
import { hasActiveSupportSession } from '@/lib/session';
import type { Cycle, PreferencesDocument } from '@/lib/types';

/**
 * The remembered-state store behind `<FilterMemory>`, `useTableFilters` and
 * `useRememberedState`.
 *
 * Module-level on purpose: `Shell` is composed per route in `App.tsx`, so a
 * component-owned copy could be torn down on navigation, and the arrival
 * bookkeeping must survive that.
 *
 * ## Two branches, two top-level preference keys
 *
 * Owner ruling 2026-10-16 (2): remembered ON THE ACCOUNT, until cleared.
 *
 * | branch  | preferences key     | entry key                | value              |
 * | ------- | ------------------- | ------------------------ | ------------------ |
 * | `url`   | `filterMemory`      | exact lowercase pathname | kept query string  |
 * | `state` | `filterMemoryState` | `<path>/table-<hash>`,   | JSON               |
 * |         |                     | `<path>/state-<id>`      |                    |
 *
 * A `state` key's `<path>` is `stateMemoryPath` (`./index.ts`): record ids
 * become `record`, so a record page keys one entry per record TYPE.
 *
 * ⚠️ **`filterMemory` is VALIDATED by the server** (2026-10-16): keys match
 * `^/[a-z0-9\-/]{0,120}$`, values are strings of at most 2000 characters,
 * at most 200 entries, and one bad key 422s the whole PATCH. So the client
 * guard (`sendable`) drops anything that would fail before it is sent, and
 * table/search state lives under the separate, unvalidated
 * `filterMemoryState` (still capped at 2000 characters per value here).
 * `PREFERENCES_TOO_LARGE` (64 KiB) and `FILTER_MEMORY_TOO_MANY` evict the
 * least-recently-used entries and retry; any other 422 drops the offending
 * keys and retries once. Nothing is ever shown to the reader for any of it.
 *
 * Values carry their own write time, `t<base36 ms>~<payload>`, so a device
 * that changed something more recently than the account copy wins on the next
 * boot instead of being overwritten by an older account value.
 *
 * `''` (payload) is a stored CLEAR, different from absent ("nothing
 * remembered"): a clear must stay cleared, so it is written like any value.
 *
 * A per-user localStorage copy is read synchronously at boot, kept in step
 * across tabs through `storage` events, and merged per entry (newest wins).
 *
 * ## Support sessions — READ, never WRITE (owner 2026-10-23)
 *
 * *SUPERSEDED: "Inert. While acting as somebody else nothing is restored and
 * nothing is written."* While acting as somebody else the store is bound to
 * THEM (`bindFilterMemoryUser(targetId, true)`) and reads their account copy
 * under the support token, so every page opens with their remembered filters,
 * sort, tab and view — the screen looks like theirs. Nothing is ever written:
 * no PATCH, no local copy, no LRU touch. A change made while acting lives in
 * this tab's memory only, until the session ends and the store re-binds to the
 * super admin's own copy. `isFilterMemoryReadable` vs `isFilterMemoryWritable`
 * is that split.
 *
 * ## Cross-device freshness
 *
 * A device WITH a local copy still waits briefly for the account read on the
 * first arrival after binding (`LOCAL_GRACE_MS`), so a sort chosen on another
 * device wins over this device's older copy. Whatever arrives later still
 * reaches an untouched table or remembered state (`useTableFilters`,
 * `useRememberedState` re-apply). Local entries newer than the account copy
 * (an unsent change — offline, a tab closed mid-debounce, a session switch)
 * are re-queued for the account once it has been read.
 */

export type MemoryBranch = 'url' | 'state';

const PREFERENCE_KEYS: Record<MemoryBranch, string> = {
  url: 'filterMemory',
  state: 'filterMemoryState',
};

const LOCAL_PREFIX = 'emerge.filterMemory.v2.';
const WRITE_DELAY_MS = 800;
/** Ceiling on waiting for the account copy when there is no local copy. */
export const ACCOUNT_LOAD_BUDGET_MS = 5000;
/** With a local copy: how long the first arrival waits for the account read. */
const LOCAL_GRACE_MS = 1000;
const MAX_VALUE_LENGTH = 2000;
export const URL_KEY_PATTERN = /^\/[a-z0-9\-/]{0,120}$/;
/** How many entries one eviction drops, at least. */
const EVICT_MIN = 20;
const RETRY_BASE_MS = 2000;
const RETRY_MAX_MS = 60000;

interface Entry {
  /** The payload, without the time prefix. */
  v: string;
  /** When it was last WRITTEN (ms). */
  t: number;
}

type Branch = Record<string, Entry>;
type Branches = Record<MemoryBranch, Branch>;
/** Last time an entry was written or restored, for LRU eviction. */
type Used = Record<string, number>;

const BRANCHES: readonly MemoryBranch[] = ['url', 'state'];

function emptyBranches(): Branches {
  return { url: {}, state: {} };
}

const state: {
  userId: number | null;
  /** Bound while acting as `userId` in a support session: read-only. */
  support: boolean;
  data: Branches;
  used: Used;
  hadLocal: boolean;
  localGraceOver: boolean;
  accountSettled: boolean;
} = {
  userId: null,
  support: false,
  data: emptyBranches(),
  used: {},
  hadLocal: false,
  localGraceOver: false,
  accountSettled: false,
};

/** Entries changed in this tab and not yet acknowledged by the server. */
let pending: Branches = emptyBranches();
let writeTimer: ReturnType<typeof setTimeout> | undefined;
let budgetTimer: ReturnType<typeof setTimeout> | undefined;
let graceTimer: ReturnType<typeof setTimeout> | undefined;
let loadController: AbortController | null = null;
let retryDelay = RETRY_BASE_MS;
let inFlight = false;
let version = 0;
const listeners = new Set<() => void>();

function emit(): void {
  version += 1;
  for (const listener of listeners) listener();
}

function safely<T>(run: () => T, fallback: T): T {
  try {
    return run();
  } catch {
    return fallback;
  }
}

/* ── Value encoding ─────────────────────────────────────────────────────── */

function encodeValue(entry: Entry): string {
  return `t${entry.t.toString(36)}~${entry.v}`;
}

function decodeValue(raw: string): Entry {
  const match = /^t([0-9a-z]{1,12})~/.exec(raw);
  if (!match || match[1] === undefined) return { v: raw, t: 0 };
  return { v: raw.slice(match[0].length), t: parseInt(match[1], 36) };
}

/* ── Keys ───────────────────────────────────────────────────────────────── */

/** The `url` key for a pathname: the exact pathname, lowercased. */
export function urlMemoryKey(pathname: string): string | null {
  const key = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  return URL_KEY_PATTERN.test(key) ? key : null;
}

/** A `state` key: compact and path-like, though that branch is unvalidated. */
export function stateMemoryKey(pathname: string, suffix: string): string {
  const base = pathname.toLowerCase().replace(/\/+$/, '');
  return `${base}/${suffix.toLowerCase().replace(/[^a-z0-9-]/g, '-')}`;
}

function usedKey(branch: MemoryBranch, key: string): string {
  return `${branch}:${key}`;
}

/** Would the server accept this entry? Anything else is never sent. */
function sendable(branch: MemoryBranch, key: string, encoded: string | null): boolean {
  if (encoded !== null && encoded.length > MAX_VALUE_LENGTH) return false;
  return branch === 'state' || URL_KEY_PATTERN.test(key);
}

/* ── Local copy ─────────────────────────────────────────────────────────── */

function localKey(userId: number): string {
  return `${LOCAL_PREFIX}${userId}`;
}

function sanitiseBranch(raw: unknown, branch: MemoryBranch): Branch {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Branch = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== 'string') continue;
    if (branch === 'url' && !URL_KEY_PATTERN.test(key)) continue;
    out[key] = decodeValue(value);
  }
  return out;
}

function sanitiseUsed(raw: unknown): Used {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Used = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
  }
  return out;
}

function readLocal(userId: number): { data: Branches; used: Used } | null {
  return safely(() => {
    const raw = localStorage.getItem(localKey(userId));
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as { u?: unknown; s?: unknown; a?: unknown } | null;
    if (parsed === null || typeof parsed !== 'object') return null;
    return {
      data: { url: sanitiseBranch(parsed.u, 'url'), state: sanitiseBranch(parsed.s, 'state') },
      used: sanitiseUsed(parsed.a),
    };
  }, null);
}

/** Newest write wins, per entry. `keep` entries (this tab's unsent) always win. */
function mergeInto(target: Branches, incoming: Branches, keep: Branches): Branches {
  const out: Branches = { url: { ...target.url }, state: { ...target.state } };
  for (const branch of BRANCHES) {
    for (const [key, entry] of Object.entries(incoming[branch])) {
      if (key in keep[branch]) continue;
      const mine = out[branch][key];
      if (mine === undefined || mine.t < entry.t) out[branch][key] = entry;
    }
  }
  return out;
}

/**
 * Write the local copy WITHOUT clobbering another tab's newer entries: what is
 * on disk is merged in first (newest wins), and `removed` is deleted outright.
 */
function writeLocal(removed: readonly [MemoryBranch, string][] = []): void {
  const userId = state.userId;
  // Acting as somebody else leaves nothing of theirs on this device.
  if (userId === null || state.support) return;
  const disk = readLocal(userId);
  if (disk) {
    for (const [branch, key] of removed) delete disk.data[branch][key];
    state.data = mergeInto(state.data, disk.data, pending);
    state.used = { ...disk.used, ...state.used };
  }
  const used: Used = {};
  for (const branch of BRANCHES) {
    for (const key of Object.keys(state.data[branch])) {
      used[usedKey(branch, key)] = state.used[usedKey(branch, key)] ?? 0;
    }
  }
  state.used = used;
  const encode = (branch: Branch) => {
    const out: Record<string, string> = {};
    for (const [key, entry] of Object.entries(branch)) out[key] = encodeValue(entry);
    return out;
  };
  safely(
    () =>
      localStorage.setItem(
        localKey(userId),
        JSON.stringify({ u: encode(state.data.url), s: encode(state.data.state), a: used }),
      ),
    undefined,
  );
}

/* ── Account copy ───────────────────────────────────────────────────────── */

function settleAccount(): void {
  if (state.accountSettled) return;
  state.accountSettled = true;
  clearTimeout(budgetTimer);
  clearTimeout(graceTimer);
  emit();
}

/**
 * Local entries the account copy does not hold, or holds OLDER: written on
 * this device and never acknowledged (offline, a tab closed mid-debounce, a
 * session switch that dropped the queue). Queued again so other devices get
 * them. Never in a support session — nothing is written there.
 */
function requeueUnsent(account: Branches): void {
  if (state.support) return;
  let queued = false;
  for (const branch of BRANCHES) {
    for (const [key, entry] of Object.entries(state.data[branch])) {
      if (key in pending[branch]) continue;
      const theirs = account[branch][key];
      if (theirs !== undefined && theirs.t >= entry.t) continue;
      if (!sendable(branch, key, encodeValue(entry))) continue;
      pending[branch][key] = entry;
      queued = true;
    }
  }
  if (queued) scheduleFlush(WRITE_DELAY_MS);
}

function loadAccount(userId: number): void {
  loadController?.abort();
  const controller = new AbortController();
  loadController = controller;
  clearTimeout(budgetTimer);
  budgetTimer = setTimeout(settleAccount, ACCOUNT_LOAD_BUDGET_MS);
  api
    .getPreferences(controller.signal)
    .then((response) => {
      if (controller.signal.aborted || state.userId !== userId) return;
      const document: PreferencesDocument = response.data.preferences;
      const account: Branches = {
        url: sanitiseBranch(document[PREFERENCE_KEYS.url], 'url'),
        state: sanitiseBranch(document[PREFERENCE_KEYS.state], 'state'),
      };
      // Per entry, the newer write wins — a local change made after the
      // account's copy (offline, or a flush that failed) is not overwritten.
      state.data = mergeInto(state.data, account, pending);
      writeLocal();
      requeueUnsent(account);
      settleAccount();
      emit();
    })
    .catch(() => {
      if (controller.signal.aborted || state.userId !== userId) return;
      settleAccount();
    });
}

/* ── The current cycle ─────────────────────────────────────────────────────
 *
 * Owner ruling 2026-10-16: a remembered cycle that WAS the current cycle when
 * stored follows the new current cycle after rollover; any other cycle was a
 * deliberate pick and stays. Cycle-named params store the then-current cycle
 * as a marker, resolved to today's current cycle on restore. Read once per
 * session; `currentCycle` (by date, `lib/document-naming.ts`) decides.
 */
let cycleState: { settled: boolean; current: Cycle | null } = { settled: false, current: null };
let cyclesRequested = false;

function loadCycles(): void {
  if (cyclesRequested) return;
  cyclesRequested = true;
  api
    .listCycles({ perPage: 100 })
    .then((response) => {
      cycleState = { settled: true, current: currentCycle(response.data) };
      emit();
    })
    .catch(() => {
      cycleState = { settled: true, current: null };
      emit();
    });
}

export function areCyclesSettled(): boolean {
  return cycleState.settled;
}

export function currentMemoryCycle(): Cycle | null {
  return cycleState.current;
}

/* ── Binding ────────────────────────────────────────────────────────────── */

/**
 * Point the store at the signed-in person. Idempotent per user; a different
 * user (sign-out/sign-in on one tab) starts from their own copy.
 */
export function bindFilterMemoryUser(userId: number | null, support = false): void {
  const inSupport = userId !== null && support;
  if (state.userId === userId && state.support === inSupport) return;
  // The previous identity's unsent queue is DROPPED, never flushed: by now the
  // token may already be the next identity's, and a flush would write one
  // person's memory into another's document. It is still in that identity's
  // local copy, and `requeueUnsent` sends it on their next bind.
  loadController?.abort();
  clearTimeout(writeTimer);
  clearTimeout(budgetTimer);
  clearTimeout(graceTimer);
  pending = emptyBranches();
  retryDelay = RETRY_BASE_MS;
  resetArrival();
  state.userId = userId;
  state.support = inSupport;
  state.accountSettled = false;
  state.localGraceOver = false;
  if (userId === null) {
    state.data = emptyBranches();
    state.used = {};
    state.hadLocal = false;
    emit();
    return;
  }
  // Acting as somebody else: their ACCOUNT copy only, never a local one.
  const local = inSupport ? null : readLocal(userId);
  state.data = local?.data ?? emptyBranches();
  state.used = local?.used ?? {};
  state.hadLocal = local !== null;
  if (state.hadLocal) {
    graceTimer = setTimeout(() => {
      state.localGraceOver = true;
      emit();
    }, LOCAL_GRACE_MS);
  }
  emit();
  // Under a support session `api` sends the support token, so this reads the
  // TARGET's document — what makes the screen look like theirs.
  loadAccount(userId);
  loadCycles();
}

export function boundFilterMemoryUser(): number | null {
  return state.userId;
}

/** Is the store bound to exactly this identity (user AND support mode)? */
export function isFilterMemoryBoundTo(userId: number | null, support: boolean): boolean {
  return state.userId === userId && state.support === (userId !== null && support);
}

/** May a page render from memory yet? */
export function isFilterMemoryReady(): boolean {
  return (
    state.userId !== null && (state.accountSettled || (state.hadLocal && state.localGraceOver))
  );
}

/**
 * May memory be READ? Signed in, and the bound mode matches the live token
 * (bound as the target only while their support session is live, and as
 * oneself only while none is) — so a switch never reads across identities.
 */
export function isFilterMemoryReadable(): boolean {
  return state.userId !== null && state.support === hasActiveSupportSession();
}

/** May memory be WRITTEN? Never while acting as somebody else. */
export function isFilterMemoryWritable(): boolean {
  return state.userId !== null && !state.support && !hasActiveSupportSession();
}

/* ── Read / write ───────────────────────────────────────────────────────── */

/** `undefined` = nothing remembered; `''` = remembered as cleared. */
export function getFilterMemory(branch: MemoryBranch, key: string): string | undefined {
  if (!isFilterMemoryReadable()) return undefined;
  return state.data[branch][key]?.v;
}

/** Mark an entry as used (a restore), so eviction keeps what is in use. */
export function touchFilterMemory(branch: MemoryBranch, key: string): void {
  if (!isFilterMemoryWritable() || !(key in state.data[branch])) return;
  state.used = { ...state.used, [usedKey(branch, key)]: Date.now() };
  writeLocal();
}

/**
 * Store a value. While acting as somebody else it is held in THIS TAB only
 * (so the page keeps what was just picked while the session lasts) and is
 * never written to their account, nor to this device.
 */
export function setFilterMemory(branch: MemoryBranch, key: string, value: string): void {
  if (!isFilterMemoryReadable()) return;
  if (!isFilterMemoryWritable()) {
    if (state.data[branch][key]?.v === value) return;
    const held: Entry = { v: value, t: Date.now() };
    state.data = { ...state.data, [branch]: { ...state.data[branch], [key]: held } };
    return;
  }
  const entry: Entry = { v: value, t: Date.now() };
  if (!sendable(branch, key, encodeValue(entry))) return;
  state.used = { ...state.used, [usedKey(branch, key)]: entry.t };
  if (state.data[branch][key]?.v === value && !(key in pending[branch])) {
    writeLocal();
    return;
  }
  state.data = { ...state.data, [branch]: { ...state.data[branch], [key]: entry } };
  pending = { ...pending, [branch]: { ...pending[branch], [key]: entry } };
  writeLocal();
  scheduleFlush(WRITE_DELAY_MS);
}

function scheduleFlush(delay: number): void {
  clearTimeout(writeTimer);
  writeTimer = setTimeout(() => flushFilterMemory(), delay);
}

/* ── Sending ────────────────────────────────────────────────────────────── */

type Patch = Record<MemoryBranch, Record<string, string | null>>;

function patchOf(batch: Branches): Patch {
  const patch: Patch = { url: {}, state: {} };
  for (const branch of BRANCHES) {
    for (const [key, entry] of Object.entries(batch[branch])) {
      const encoded = encodeValue(entry);
      if (sendable(branch, key, encoded)) patch[branch][key] = encoded;
    }
  }
  return patch;
}

function isEmptyPatch(patch: Patch): boolean {
  return BRANCHES.every((branch) => Object.keys(patch[branch]).length === 0);
}

function send(patch: Patch, keepalive: boolean) {
  const preferences: Record<string, Record<string, string | null>> = {};
  for (const branch of BRANCHES) {
    if (Object.keys(patch[branch]).length > 0) preferences[PREFERENCE_KEYS[branch]] = patch[branch];
  }
  return api.updatePreferences({ preferences }, undefined, keepalive);
}

/** Put a failed batch back, never over a newer value queued since. */
function requeue(batch: Branches): void {
  for (const branch of BRANCHES) {
    for (const [key, entry] of Object.entries(batch[branch])) {
      if (key in pending[branch]) continue;
      pending[branch][key] = entry;
    }
  }
}

/** The least-recently-used entries outside `keep`. */
function evictionSet(keep: Patch): [MemoryBranch, string][] {
  const all: [MemoryBranch, string][] = [];
  for (const branch of BRANCHES) {
    for (const key of Object.keys(state.data[branch])) {
      if (!(key in keep[branch])) all.push([branch, key]);
    }
  }
  all.sort(
    ([ba, ka], [bb, kb]) =>
      (state.used[usedKey(ba, ka)] ?? 0) - (state.used[usedKey(bb, kb)] ?? 0),
  );
  const count = Math.max(EVICT_MIN, Math.ceil(all.length / 4));
  return all.slice(0, count);
}

function evict(entries: [MemoryBranch, string][], patch: Patch): void {
  const data: Branches = { url: { ...state.data.url }, state: { ...state.data.state } };
  for (const [branch, key] of entries) {
    delete data[branch][key];
    patch[branch][key] = null;
  }
  state.data = data;
  writeLocal(entries);
}

/** Keys a validation 422 names, read from `details[].path`. */
function offendingKeys(caught: ApiError, patch: Patch): [MemoryBranch, string][] {
  const out: [MemoryBranch, string][] = [];
  const paths = (caught.details ?? []).map(
    (detail) => `${detail.path} ${'key' in detail ? String(detail.key) : ''}`,
  );
  for (const branch of BRANCHES) {
    for (const key of Object.keys(patch[branch])) {
      if (paths.some((path) => path.includes(key))) out.push([branch, key]);
    }
  }
  return out;
}

const CAPACITY_CODES = new Set(['FILTER_MEMORY_TOO_MANY', 'PREFERENCES_TOO_LARGE']);

/**
 * Send whatever is pending. `keepalive` for a page that is going away.
 *
 * - 2xx: done; the backoff resets.
 * - 422 capacity: evict LRU entries (deleted in the same PATCH), retry once.
 * - 422 other: drop the keys it names (all of the batch if it names none we
 *   sent), retry once with the rest.
 * - other 4xx (401, 403, 413…): dropped; retrying cannot change the answer.
 * - network, 429, 5xx: re-queued under newer values, retried with backoff.
 */
export function flushFilterMemory(keepalive = false): void {
  clearTimeout(writeTimer);
  if (inFlight && !keepalive) {
    scheduleFlush(WRITE_DELAY_MS);
    return;
  }
  const batch = pending;
  pending = emptyBranches();
  const patch = patchOf(batch);
  if (isEmptyPatch(patch)) return;
  // Checked at send time too: a session started after the change was queued
  // must not carry the super admin's memory into the target's document. (The
  // batch stays in the local copy; `requeueUnsent` sends it on the next bind.)
  if (state.support || hasActiveSupportSession()) return;
  const userId = state.userId;
  inFlight = true;

  const succeed = () => {
    inFlight = false;
    retryDelay = RETRY_BASE_MS;
  };
  const backoff = () => {
    inFlight = false;
    if (state.userId !== userId) return;
    requeue(batch);
    scheduleFlush(retryDelay);
    retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS);
  };

  send(patch, keepalive)
    .then(succeed)
    .catch((caught: unknown) => {
      if (state.userId !== userId) {
        inFlight = false;
        return;
      }
      if (!(caught instanceof ApiError)) return backoff();
      if (caught.status === 429 || caught.status >= 500 || caught.status === 0) return backoff();
      if (caught.status !== 422) {
        inFlight = false;
        return;
      }
      const retry: Patch = { url: { ...patch.url }, state: { ...patch.state } };
      if (CAPACITY_CODES.has(caught.code)) {
        evict(evictionSet(retry), retry);
      } else {
        const named = offendingKeys(caught, retry);
        if (named.length === 0) {
          inFlight = false;
          return;
        }
        for (const [branch, key] of named) delete retry[branch][key];
      }
      if (isEmptyPatch(retry)) {
        inFlight = false;
        return;
      }
      send(retry, false)
        .then(succeed)
        .catch(() => {
          // Retried once already: silent by design; the local copy keeps it.
          inFlight = false;
        });
    });
}

export function subscribeFilterMemory(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function filterMemoryVersion(): number {
  return version;
}

/* ── Arrivals ───────────────────────────────────────────────────────────── */

/**
 * How the reader arrived at the page now open.
 *
 * - `link`: the URL carried params (other than `page`/`perPage`). Ruling 3:
 *   shown exactly, and READ-ONLY for memory — only real user edits after it
 *   are stored, and the state hooks start from their fallbacks.
 * - `bare`: no params, and nothing to restore (nothing remembered, or a
 *   remembered clear).
 * - `restored`: the remembered address was put back before the page mounted.
 * - `restored-partial`: restored, but a current-cycle marker could not be
 *   resolved in time; the page's own current-cycle default fills it.
 */
export type ArrivalKind = 'link' | 'bare' | 'restored' | 'restored-partial';

export interface ArrivalRecord {
  path: string;
  /** `location.key` at arrival — a PUSH to the same bare path is a new one. */
  key: string;
  kind: ArrivalKind;
  /** The kept query the page held once the arrival settled. */
  kept: string;
}

let arrival: ArrivalRecord | null = null;

export function getArrival(): ArrivalRecord | null {
  return arrival;
}

export function setArrival(record: ArrivalRecord | null): void {
  arrival = record;
}

function resetArrival(): void {
  arrival = null;
}

/* ── User input, to tell a reader's edit from an arrival writer's ───────── */

let lastUserInputAt = 0;
/** An edit this soon after a key press, click or input event is the reader's. */
const USER_EDIT_WINDOW_MS = 1500;

export function isRecentUserInput(): boolean {
  return Date.now() - lastUserInputAt <= USER_EDIT_WINDOW_MS;
}

if (typeof window !== 'undefined') {
  const markInput = () => {
    lastUserInputAt = Date.now();
  };
  for (const type of ['pointerdown', 'keydown', 'input', 'change']) {
    window.addEventListener(type, markInput, { capture: true, passive: true });
  }
  window.addEventListener('pagehide', () => flushFilterMemory(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushFilterMemory(true);
  });
  // Another tab wrote its copy: take its newer entries (ours unsent win).
  window.addEventListener('storage', (event) => {
    const userId = state.userId;
    if (userId === null || state.support || event.key !== localKey(userId)) return;
    const disk = readLocal(userId);
    if (!disk) return;
    state.data = mergeInto(state.data, disk.data, pending);
    state.used = { ...disk.used, ...state.used };
    emit();
  });
}
